import type { IGetCompareDateByAlias } from '../types';
import type { Dayjs, ManipulateType } from 'dayjs';

import { MainDateTimePickerAlias } from '../constants';
import { dayjs } from '~/utils/dayjs';

export const getNoComparison: IGetCompareDateByAlias = () => {
  return {
    since: 0,
    until: 0,
  };
};

export const getPreviousPeriod: IGetCompareDateByAlias = (value) => {
  const { since, until, alias } = value;
  const sinceDate = dayjs(since);
  const untilDate = dayjs(until);

  if (alias === MainDateTimePickerAlias.LAST_MONTH) {
    return getLastMonth(sinceDate);
  }

  if (alias === MainDateTimePickerAlias.LAST_12_MONTHS) {
    return getLast12Months(sinceDate);
  }

  if (alias === MainDateTimePickerAlias.LAST_YEAR) {
    return getLastYear(sinceDate);
  }

  const duration = untilDate.diff(sinceDate, 'day') + 1;
  const previousSince = sinceDate.subtract(duration, 'day');
  const previousUntil = untilDate.subtract(duration, 'day');
  return {
    since: previousSince.valueOf(),
    until: previousUntil.valueOf(),
  };
};

const createPreviousRange =
  (subtractAmount: number, subtractUnit: ManipulateType) =>
  (value: { since: number | string; until: number | string }) => {
    const { since, until } = value;
    const sinceDate = dayjs(since);
    const untilDate = dayjs(until);
    const duration = untilDate.diff(sinceDate, 'day');
    const previousSince = sinceDate.subtract(subtractAmount, subtractUnit);
    const previousUntil = previousSince.add(duration, 'day');
    return {
      since: previousSince.valueOf(),
      until: previousUntil.valueOf(),
    };
  };

export const getPreviousWeek = createPreviousRange(7, 'day');
export const getPreviousQuarter = createPreviousRange(3, 'month');
export const getPreviousMonth = createPreviousRange(1, 'month');
export const getPreviousYear = createPreviousRange(1, 'year');

export const createLastDaysRange = (subtractAmount: number) => (date: Dayjs) => {
  return {
    since: date.subtract(subtractAmount, 'day').valueOf(),
    until: date.valueOf(),
  };
};
export const getToday = (currentDate: Dayjs) => {
  return {
    since: currentDate.valueOf(),
    until: currentDate.valueOf(),
  };
};

export const getYesterday = (date: Dayjs) => {
  return {
    since: date.subtract(1, 'day').valueOf(),
    until: date.subtract(1, 'day').valueOf(),
  };
};

export const getLast7Days = createLastDaysRange(6);

export const getLast30Days = createLastDaysRange(29);

export const getLast90Days = createLastDaysRange(89);

export const getLast365Days = createLastDaysRange(364);

export function getLastMonth(date: Dayjs) {
  return {
    since: date.subtract(1, 'month').startOf('month').valueOf(),
    until: date.subtract(1, 'month').endOf('month').valueOf(),
  };
}

export function getLast12Months(date: Dayjs) {
  return {
    since: date.subtract(12, 'month').startOf('month').valueOf(),
    until: date.subtract(1, 'month').endOf('month').valueOf(),
  };
}

export function getLastYear(date: Dayjs) {
  return {
    since: date.subtract(1, 'year').startOf('year').valueOf(),
    until: date.subtract(1, 'year').endOf('year').valueOf(),
  };
}

export const getMonthAndYearByDateFilter = (month: number, year: number, date: Date) => {
  const endDate = dayjs(date);
  const endMonthFilter = endDate.month();
  const endYearFilter = endDate.year();

  if (endYearFilter !== year || endMonthFilter !== month) {
    const prev = endDate.subtract(1, 'month');
    return {
      month: prev.month(),
      year: prev.year(),
    };
  }

  return { month, year };
};
